/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import styled from 'styled-components';

import { LinearProgress } from '@mui/material';
import { useTheme } from '../../styles/theme';
import { DataGrid, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { TableToolbar } from './TableToolbar';
import { ToolbarDateSelector } from './TableToolbarDate';

type TableContainerProps = {
  width?: string;
  height?: string;
  noHeader?: boolean;
  noAutoWidth?: boolean;
  disabled?: boolean;
};

const Container = styled.div<TableContainerProps>`
  width: ${({ width, noAutoWidth }) => (width ? width : noAutoWidth ? undefined : 'calc(100vw - 61px - 48px)')};
  height: ${({ height }) => height || 'calc(100vh - 61px - 48px)'};
  background-color: ${({ theme }) => theme.background.standard};

  & .MuiDataGrid-cell:hover {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed !important' : 'pointer')};
  }

  & .MuiDataGrid-footerContainer {
    max-height: 63px;
    min-height: 63px;
    background-color: ${({ theme }) => theme.background.standard};
  }

  & .MuiDataGrid-columnHeaders {
    background-color: ${({ theme }) => theme.background.standard};
  }
`;

interface Props {
  // the rows of data populating the table
  data: any;
  // the column specification for the mui data-grid https://mui.com/x/react-data-grid/column-definition/
  columns: any;
  // a function to get the row that represents the primary key for the table
  getRowId: (row: any) => string;
  // the density of the rows in the table
  density?: 'compact' | 'standard' | 'comfortable';
  // if the table is loading
  loading?: boolean;
  // the width of the table (e.g '1000px')
  width?: string;
  // the height of the table (e.g '300px')
  height?: string;
  // a function to handle clicking a row
  onRowClick?: (row: any) => void;
  // a function to handle double clicking a row
  onRowDoubleClick?: (row: any) => void;
  // a function to handle if certain rows are disabled from selection
  isRowSelectable?: (params: GridRowParams<any>) => boolean;
  // show checkboxes next to each row
  checkboxes?: boolean;
  // an array of rows currently selected in the table
  selectionModel?: GridRowSelectionModel;
  // a function to be used with checkboxes to handle when a checkbox is enabled/disabled
  onSelectionModelChange?: (newSelectionModel: GridRowSelectionModel) => void;
  // set to true to remove the header from the table
  noHeader?: boolean;
  // a row ID to sort the table by default on render
  sortModel?: string;
  // initial state for the table (sorting, pagination, etc)
  initialState?: any;
  // pagination model for controlling current page and page size
  paginationModel?: {
    page: number;
    pageSize: number;
  };
  // callback when pagination changes
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  // hide the toolbar
  hideToolbar?: boolean;
  // Only show the search bar in the toolbar
  toolbarSearchOnly?: boolean;
  // add a margin around the toolbar search bar
  toolbarSearchMargin?: number;
  // the prefix for the export file name
  exportPrefix?: string;
  // define to show a date range picker in the toolbar
  dateRange?: ToolbarDateSelector;
  // define to not automatically set the width of the table to the width of the container
  noAutoWidth?: boolean;
  // define to disable row click and double click
  disabled?: boolean;
}

export const Table: React.FC<Props> = ({
  data,
  columns,
  density,
  loading,
  getRowId,
  onRowClick,
  onRowDoubleClick,
  isRowSelectable,
  width,
  height,
  hideToolbar = false,
  toolbarSearchOnly = false,
  toolbarSearchMargin = 12,
  exportPrefix = 'table',
  dateRange,
  checkboxes,
  noHeader,
  sortModel,
  selectionModel = [],
  initialState,
  onSelectionModelChange = () => null,
  paginationModel,
  onPaginationModelChange,
  noAutoWidth = false,
  disabled = false,
}) => {
  const theme = useTheme();

  return (
    <Container width={width} noAutoWidth={noAutoWidth} height={height} noHeader={noHeader} disabled={disabled}>
      <DataGrid
        sx={{
          backgroundColor: theme.background.standard,
          border: 0,
        }}
        rows={Array.isArray(data) ? data : []}
        columns={columns}
        getRowId={getRowId}
        disableRowSelectionOnClick={!checkboxes}
        checkboxSelection={checkboxes}
        onRowClick={!disabled ? onRowClick : undefined}
        onRowDoubleClick={!disabled ? onRowDoubleClick : undefined}
        slots={{
          loadingOverlay: LinearProgress,
          toolbar: hideToolbar ? undefined : TableToolbar,
        }}
        slotProps={{
          toolbar: { toolbarSearchOnly, exportPrefix, dateRange, toolbarSearchMargin },
        }}
        isRowSelectable={isRowSelectable}
        loading={loading}
        initialState={{
          sorting: {
            sortModel: [{ field: sortModel || 'last_modification', sort: 'desc' }],
          },
          pagination: {
            paginationModel: {
              page: paginationModel?.page || 0,
              pageSize: paginationModel?.pageSize || 15,
            },
          },
          ...initialState,
        }}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        autoPageSize
        density={density || 'standard'}
        columnHeaderHeight={noHeader ? 0 : 56}
        rowSelectionModel={disabled ? [] : selectionModel}
        onRowSelectionModelChange={onSelectionModelChange}
      />
    </Container>
  );
};
